import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BookOpen, ChevronLeft, Loader2, RotateCcw } from 'lucide-react'
import toast from 'react-hot-toast'
import VideoaulaCard from '../components/VideoaulaCard'
import api, { getApiError } from '../services/api'
import { useAuthStore } from '../stores/authStore'

function TemaPage() {
  const { id } = useParams()
  const queryClient = useQueryClient()
  const accessToken = useAuthStore(state => state.accessToken)
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['tema', id],
    queryFn: async () => {
      const temaResponse = await api.get(`/temas/${id}`)
      const tema = temaResponse.data.tema
      const buscaResponse = await api.get('/search', {
        params: { q: tema.nome, materia_id: tema.modulo?.materia?.id }
      })
      return {
        tema,
        videoaulas: buscaResponse.data.videoaulas,
        youtubeErro: buscaResponse.data.youtube_erro
      }
    }
  })

  const agendarRevisao = useMutation({
    mutationFn: () => api.post('/revisoes', { tema_id: id }),
    onSuccess: ({ data }) => {
      toast.success(data.message)
      queryClient.invalidateQueries({ queryKey: ['revisoes'] })
    },
    onError: mutationError => toast.error(getApiError(mutationError, 'Não foi possível agendar a revisão.'))
  })

  if (isLoading) return <p className="text-sm text-gray-500">Carregando tema...</p>

  if (isError) {
    return (
      <div className="card text-sm">
        <p className="text-red-600">{getApiError(error, 'Não foi possível carregar este tema.')}</p>
        <button type="button" className="btn-secondary mt-3" onClick={() => refetch()}>Tentar novamente</button>
      </div>
    )
  }

  const { tema, videoaulas, youtubeErro } = data

  return (
    <div className="space-y-5">
      <Link
        to={`/materia/${tema.modulo?.materia?.id}`}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-black"
      >
        <ChevronLeft size={14} /> Voltar à matéria
      </Link>

      <div>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span>{tema.modulo?.materia?.nome}</span><span>→</span><span>{tema.modulo?.nome}</span>
        </div>
        <h1 className="text-lg font-medium leading-tight lg:text-2xl">{tema.nome}</h1>
        {tema.descricao && <p className="text-sm text-gray-500 mt-1">{tema.descricao}</p>}
      </div>

      <div className="card bg-gray-50/50 lg:max-w-3xl">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen size={16} className="text-gray-600" />
          <h2 className="text-sm font-medium">Sobre o tema</h2>
        </div>
        <p className="text-sm text-gray-600 leading-relaxed">
          {tema.descricao || 'O resumo deste tema será adicionado em breve.'}
        </p>
        <div className="flex gap-2 mt-3 text-xs text-gray-500">
          <span className="bg-white border border-gray-200 rounded-full px-2 py-1">Nível {tema.nivel_dificuldade}</span>
          {tema.tempo_estimado_minutos && (
            <span className="bg-white border border-gray-200 rounded-full px-2 py-1">{tema.tempo_estimado_minutos} min</span>
          )}
        </div>
        {accessToken ? (
          <button
            type="button"
            onClick={() => agendarRevisao.mutate()}
            disabled={agendarRevisao.isLoading}
            className="btn-secondary mt-4 inline-flex items-center gap-2"
          >
            {agendarRevisao.isLoading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
            {agendarRevisao.isLoading ? 'Adicionando...' : 'Adicionar à revisão'}
          </button>
        ) : (
          <Link to="/login" className="btn-secondary mt-4 inline-flex items-center gap-2">
            <RotateCcw size={15} /> Entre para agendar a revisão
          </Link>
        )}
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium">Videoaulas encontradas agora</h2>
          <span className="text-xs text-gray-400">{videoaulas.length} {videoaulas.length === 1 ? 'opção' : 'opções'}</span>
        </div>
        {youtubeErro && (
          <div className="card border-amber-200 bg-amber-50 text-sm text-amber-800 mb-3">
            {youtubeErro}
          </div>
        )}
        {videoaulas.length ? (
          <div className="grid gap-3 xl:grid-cols-2">
            {videoaulas.map(video => <VideoaulaCard key={video.id} videoaula={video} />)}
          </div>
        ) : (
          !youtubeErro && <p className="card text-sm text-gray-500">Nenhuma videoaula encontrada para este tema.</p>
        )}
      </section>
    </div>
  )
}

export default TemaPage
